import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore'; // Removed deleteDoc as it's handled by function
import { firestore, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { TrashIcon, CogIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import TestDataProviderGenerator from '../../components/admin/TestDataProviderGenerator';
import type {
  BusinessHours,
  ServiceCapabilities,
  ServiceProviderPricing,
} from '../../types/firestore';
import {
  formatServiceHourlyRateLabel,
  getPublicServiceHourlyRate,
} from '../../utils/servicePricing';

interface TestServiceProvider {
  id: string; // Document ID
  serviceName: string;
  companyName?: string;
  email: string;
  createdAt: Timestamp;
  isTestData: boolean;
  city?: string;
  address?: {
    city?: string;
  };
  servicePricing?: ServiceProviderPricing | null;
  businessHours?: BusinessHours | null;
  serviceCapabilities?: ServiceCapabilities | null;
  averageRating?: number;
  reviewsCount?: number;
  numberOfRatings?: number;
}

// Define the callable function for deletion
const deleteTestServiceProviderFn = httpsCallable<
  { serviceProviderId: string },
  { success: boolean; message: string; deletedReviews?: number }
>(functions, 'deleteTestServiceProvider');
const deleteAllTestServiceProvidersFn = httpsCallable<
  { expectedCount: number },
  {
    success: boolean;
    message: string;
    deletedCount: number;
    matchedCount: number;
    deletedProviders?: number;
    deletedReviews?: number;
  }
>(functions, 'deleteAllTestServiceProviders');

const AdminTestDataPage: React.FC = () => {
  const [testProviders, setTestProviders] = useState<TestServiceProvider[]>([]);
  const [loadingList, setLoadingList] = useState<boolean>(true);
  const [listError, setListError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmBulkModalOpen, setConfirmBulkModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<{ id: string; name: string } | null>(
    null
  );
  const getProviderCity = (provider: TestServiceProvider) =>
    provider.city || provider.address?.city || 'Brak miasta';
  const getProviderHourlyRateLabel = (provider: TestServiceProvider) => {
    const publicHourlyRate = getPublicServiceHourlyRate({
      servicePricing: provider.servicePricing || null,
    });

    return publicHourlyRate ? formatServiceHourlyRateLabel(publicHourlyRate.amount) : 'Brak stawki';
  };
  const getProviderReviewsMeta = (provider: TestServiceProvider) => {
    const reviewsCount = provider.reviewsCount ?? provider.numberOfRatings ?? 0;
    if (reviewsCount <= 0) {
      return 'Brak opinii';
    }

    const averageRating = Number(provider.averageRating || 0)
      .toFixed(1)
      .replace('.', ',');
    const reviewLabel = reviewsCount === 1 ? 'opinia' : reviewsCount < 5 ? 'opinie' : 'opinii';
    return `${averageRating} / 5 · ${reviewsCount} ${reviewLabel}`;
  };
  const hasBusinessHoursConfigured = (provider: TestServiceProvider) =>
    Boolean(provider.businessHours && typeof provider.businessHours.weekly === 'object');
  const getProviderCapabilitiesMeta = (provider: TestServiceProvider) => {
    const capabilities = provider.serviceCapabilities;
    const categoryCount = capabilities?.index?.categoryIds?.length || 0;
    const objectCount = capabilities?.index?.objectIds?.length || 0;
    if (!capabilities?.items?.length || categoryCount <= 0) {
      return 'Brak Smart Search';
    }
    return objectCount > 0
      ? `Smart Search: ${categoryCount} obsz. / ${objectCount} rzeczy`
      : `Smart Search: ${categoryCount} obsz.`;
  };

  // Fetch test service providers
  useEffect(() => {
    setLoadingList(true);
    setListError(null);
    const q = query(collection(firestore, 'serviceProviders'), where('isTestData', '==', true));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const providers: TestServiceProvider[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          providers.push({
            id: doc.id,
            serviceName: data.serviceName || data.companyName || 'Brak nazwy',
            companyName: data.companyName || undefined,
            email: data.email || data.contactEmail || 'Brak emaila',
            createdAt: data.createdAt || Timestamp.now(),
            isTestData: data.isTestData,
            city: data.city || data.address?.city || undefined,
            address: data.address || undefined,
            servicePricing: data.servicePricing || null,
            businessHours: data.businessHours || null,
            serviceCapabilities: data.serviceCapabilities || null,
            averageRating: data.averageRating || 0,
            reviewsCount: data.reviewsCount,
            numberOfRatings: data.numberOfRatings,
          } as TestServiceProvider);
        });
        providers.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
        setTestProviders(providers);
        setLoadingList(false);
      },
      (err) => {
        console.error('Error fetching test service providers:', err);
        setListError('Nie udało się załadować listy testowych serwisantów.');
        setLoadingList(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Initiate deletion process by opening modal
  const handleDeleteClick = (provider: { id: string; name: string }) => {
    setProviderToDelete(provider);
    setDeleteError(null);
    setSuccessMessage(null);
    setConfirmModalOpen(true);
  };

  // Handle the actual deletion after confirmation
  const handleConfirmDelete = async () => {
    const provider = providerToDelete; // Capture value for closure
    if (!provider) return;

    setConfirmModalOpen(false);
    setDeletingId(provider.id);
    setDeleteError(null);
    setSuccessMessage(null);
    try {
      const result = await deleteTestServiceProviderFn({ serviceProviderId: provider.id });
      console.log(`Successfully called delete function for ${provider.id}`);
      setSuccessMessage(result.data?.message || 'Serwisant testowy został usunięty.');
      // List will update via onSnapshot
    } catch (err: any) {
      console.error(`Error deleting test provider ${provider.id}:`, err);
      setDeleteError(`Nie udało się usunąć serwisanta. Błąd: ${err.message || 'Nieznany błąd'}`);
    } finally {
      setDeletingId(null);
      setProviderToDelete(null); // Clear the provider to delete
    }
  };

  const handleBulkDeleteClick = () => {
    setDeleteError(null);
    setSuccessMessage(null);
    setConfirmBulkModalOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    const currentCount = testProviders.length;
    setConfirmBulkModalOpen(false);
    setBulkDeleting(true);
    setDeleteError(null);
    setSuccessMessage(null);

    try {
      const result = await deleteAllTestServiceProvidersFn({ expectedCount: currentCount });
      const deletedProviders = result.data?.deletedProviders ?? result.data?.deletedCount ?? 0;
      const deletedReviews = result.data?.deletedReviews ?? 0;
      setSuccessMessage(
        result.data?.message ||
          `Usunięto ${deletedProviders} testowych serwisów i ${deletedReviews} demonstracyjnych opinii.`
      );
    } catch (err: any) {
      console.error('Error bulk deleting test providers:', err);
      setDeleteError(
        `Nie udało się usunąć wszystkich testowych serwisantów. Błąd: ${err.message || 'Nieznany błąd'}`
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ border: '1px solid rgba(51, 65, 85, 0.7)', borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <CogIcon className="mr-2 h-6 w-6" /> Narzędzia
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
          Moduły techniczne i utrzymaniowe panelu admina. Dane testowe są celowo poza głównym
          pulpitem operacyjnym.
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Dane testowe
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.75 }}>
          Generator tworzy profile testowe z cenami, godzinami, sposobami realizacji i opiniami
          demonstracyjnymi.
        </Typography>
        <TestDataProviderGenerator />
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            padding: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h6">
            Istniejący Testowi Serwisanci ({testProviders.length})
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleBulkDeleteClick}
            disabled={loadingList || bulkDeleting || testProviders.length === 0 || !!deletingId}
            startIcon={
              bulkDeleting ? <CircularProgress size={18} /> : <TrashIcon className="h-4 w-4" />
            }
          >
            {bulkDeleting ? 'Usuwanie...' : 'Usuń wszystkich testowych serwisantów'}
          </Button>
        </Box>
        {deleteError && (
          <Alert severity="error" sx={{ margin: 2 }}>
            {deleteError}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ margin: 2 }}>
            {successMessage}
          </Alert>
        )}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
          {loadingList && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {listError && <Alert severity="error">{listError}</Alert>}
          {!loadingList && !listError && testProviders.length === 0 && (
            <Alert severity="info">Brak testowych serwisantów w bazie.</Alert>
          )}
          {!loadingList &&
            !listError &&
            testProviders.map((provider) => (
              <Paper
                key={provider.id}
                variant="outlined"
                sx={{ p: 2, mb: 2, borderColor: 'rgba(148,163,184,0.25)' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {provider.serviceName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {provider.email}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.75 }}>
                  {getProviderCity(provider)} · {getProviderHourlyRateLabel(provider)}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
                  {getProviderCapabilitiesMeta(provider)}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
                  {getProviderReviewsMeta(provider)}
                  {hasBusinessHoursConfigured(provider) ? ' · Godziny ustawione' : ''}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.7 }}>
                  Utworzono: {provider.createdAt.toDate().toLocaleString('pl-PL')}
                </Typography>
                <Chip size="small" label="Dane testowe" sx={{ mt: 1 }} />
                <Box sx={{ mt: 1.5 }}>
                  {deletingId === provider.id || bulkDeleting ? (
                    <CircularProgress size={22} />
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<TrashIcon className="h-4 w-4" />}
                      onClick={() =>
                        handleDeleteClick({ id: provider.id, name: provider.serviceName })
                      }
                      disabled={!!deletingId || bulkDeleting}
                    >
                      Usuń
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer>
            <Table stickyHeader aria-label="test service providers table">
              <TableHead>
                <TableRow>
                  <TableCell>Nazwa serwisu</TableCell>
                  <TableCell>Email Kontaktowy</TableCell>
                  <TableCell>Meta</TableCell>
                  <TableCell>Data Utworzenia</TableCell>
                  <TableCell align="right">Akcje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : listError ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Alert severity="error">{listError}</Alert>
                    </TableCell>
                  </TableRow>
                ) : testProviders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Brak testowych serwisantów w bazie.
                    </TableCell>
                  </TableRow>
                ) : (
                  testProviders.map((provider) => (
                    <TableRow key={provider.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {provider.serviceName}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.72 }}>
                          {getProviderCity(provider)}
                        </Typography>
                      </TableCell>
                      <TableCell>{provider.email}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" sx={{ opacity: 0.82 }}>
                            {getProviderHourlyRateLabel(provider)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.82 }}>
                            {getProviderCapabilitiesMeta(provider)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.82 }}>
                            {getProviderReviewsMeta(provider)}
                          </Typography>
                          {hasBusinessHoursConfigured(provider) && (
                            <Typography variant="caption" sx={{ opacity: 0.68 }}>
                              Godziny ustawione
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{provider.createdAt.toDate().toLocaleString('pl-PL')}</TableCell>
                      <TableCell align="right">
                        {deletingId === provider.id || bulkDeleting ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<TrashIcon className="h-4 w-4" />}
                            onClick={() =>
                              handleDeleteClick({ id: provider.id, name: provider.serviceName })
                            }
                            disabled={!!deletingId || bulkDeleting}
                          >
                            Usuń
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Confirmation Modal */}
      {providerToDelete && (
        <ConfirmationModal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Potwierdź Usunięcie"
          message={
            <>
              Czy na pewno chcesz trwale usunąć testowego serwisanta{' '}
              <strong>{providerToDelete.name}</strong> ({providerToDelete.id}) wraz z jego
              demonstracyjnymi opiniami? Tej operacji nie można cofnąć.
            </>
          }
          confirmText="Usuń"
          confirmColor="error"
        />
      )}

      <ConfirmationModal
        open={confirmBulkModalOpen}
        onClose={() => setConfirmBulkModalOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        title="Potwierdź hurtowe usuwanie"
        message={
          <>
            Czy na pewno chcesz trwale usunąć <strong>{testProviders.length}</strong> rekordów
            serwisantów testowych?
            <br />
            Operacja obejmuje wyłącznie dokumenty z kolekcji <strong>serviceProviders</strong>{' '}
            oznaczone jako <strong>isTestData = true</strong> oraz powiązane z nimi testowe opinie.
          </>
        }
        confirmText="Usuń wszystkie testowe"
        confirmColor="error"
      />
    </Box>
  );
};

export default AdminTestDataPage;
